/**
 * Erros distinguíveis (SKILL §21.1). A UI mostra mensagem legível; o detalhe
 * técnico fica disponível mas não é despejado na cara do usuário.
 */

export type AppErrorKind =
  | 'unauthorized'
  | 'forbidden'
  | 'validation'
  | 'conflict'
  | 'not_found'
  | 'network'
  | 'offline'
  | 'server_error';

export class AppError extends Error {
  readonly kind: AppErrorKind;
  readonly detail?: string;
  override readonly cause?: unknown;

  constructor(kind: AppErrorKind, message: string, detail?: string, cause?: unknown) {
    super(message);
    this.name = 'AppError';
    this.kind = kind;
    this.detail = detail;
    this.cause = cause;
  }

  /** Erros que vale a pena reenviar automaticamente. */
  get retryable(): boolean {
    return this.kind === 'network' || this.kind === 'offline' || this.kind === 'server_error';
  }
}

interface PostgrestLike {
  code?: string;
  message?: string;
  details?: string;
  hint?: string;
  status?: number;
}

const MESSAGES: Record<AppErrorKind, string> = {
  unauthorized: 'Sessão expirada. Entre novamente.',
  forbidden: 'Você não tem permissão para esta ação.',
  validation: 'Não foi possível salvar: verifique os dados informados.',
  conflict: 'Este registro foi alterado por outra pessoa.',
  not_found: 'Registro não encontrado.',
  network: 'Sem conexão com o servidor.',
  offline: 'Sem conexão. A alteração ficou na fila e será enviada depois.',
  server_error: 'Erro no servidor. Tente novamente.',
};

/**
 * Erros do GoTrue (login, cadastro, recuperação).
 *
 * Eles NÃO parecem erro de Postgres: vêm com `code` alfabético e status 400,
 * que nenhuma regra abaixo pegava. O resultado era que senha errada dizia
 * "Erro no servidor. Tente novamente." — mentira que manda a pessoa tentar
 * de novo a mesma senha errada, e esconde o problema real.
 */
const AUTH_ERRORS: Record<string, [AppErrorKind, string]> = {
  invalid_credentials: ['unauthorized', 'E-mail ou senha incorretos.'],
  invalid_grant: ['unauthorized', 'E-mail ou senha incorretos.'],
  email_not_confirmed: [
    'unauthorized',
    'E-mail ainda não confirmado. Procure a mensagem de confirmação na caixa de entrada.',
  ],
  user_not_found: ['unauthorized', 'E-mail ou senha incorretos.'],
  user_already_exists: ['conflict', 'Já existe uma conta com este e-mail.'],
  email_exists: ['conflict', 'Já existe uma conta com este e-mail.'],
  weak_password: ['validation', 'Senha fraca. Use pelo menos 8 caracteres.'],
  over_request_rate_limit: [
    'server_error',
    'Muitas tentativas seguidas. Espere um minuto e tente de novo.',
  ],
  over_email_send_rate_limit: [
    'server_error',
    'Muitas mensagens enviadas para este e-mail. Espere um minuto.',
  ],
  signup_disabled: ['forbidden', 'O cadastro está desativado nesta instalação.'],
  session_expired: ['unauthorized', MESSAGES.unauthorized],
};

/** Nomes que o supabase-js dá ao erro quando o servidor não respondeu. */
const FETCH_ERROR_NAMES = new Set(['AuthRetryableFetchError', 'FunctionsFetchError']);

export function toAppError(raw: unknown): AppError {
  if (raw instanceof AppError) return raw;

  if (raw instanceof TypeError && /fetch|network/i.test(raw.message)) {
    return new AppError('network', MESSAGES.network, raw.message, raw);
  }

  const e = raw as PostgrestLike & { name?: string; error_code?: string };

  // O supabase-js embrulha a falha de rede num erro próprio, que não é
  // TypeError — sem isto, servidor fora do ar virava "erro no servidor".
  if (
    (e?.name && FETCH_ERROR_NAMES.has(e.name)) ||
    (e?.status === 0 && typeof e?.message === 'string')
  ) {
    return new AppError('network', MESSAGES.network, e?.message, raw);
  }

  const authCode = e?.error_code ?? (typeof e?.code === 'string' ? e.code : '');
  const mapped = authCode ? AUTH_ERRORS[authCode] : undefined;
  if (mapped) {
    return new AppError(mapped[0], mapped[1], e?.message, raw);
  }

  const code = e?.code ?? '';
  const detail = [e?.message, e?.details, e?.hint].filter(Boolean).join(' · ');

  // Postgres / PostgREST
  if (code === '42501' || e?.status === 403) {
    return new AppError('forbidden', MESSAGES.forbidden, detail, raw);
  }
  if (code === '28000' || e?.status === 401) {
    return new AppError('unauthorized', MESSAGES.unauthorized, detail, raw);
  }
  if (code === 'PGRST116' || e?.status === 404) {
    return new AppError('not_found', MESSAGES.not_found, detail, raw);
  }
  if (code === '23505') {
    return new AppError('conflict', 'Já existe um registro com este identificador.', detail, raw);
  }
  if (code.startsWith('23') || code === '22P02') {
    return new AppError('validation', MESSAGES.validation, detail, raw);
  }
  if (code === '40001' || code === '40P01') {
    return new AppError('conflict', MESSAGES.conflict, detail, raw);
  }
  if (e?.status === 429) {
    return new AppError(
      'server_error',
      'Muitas tentativas seguidas. Espere um minuto e tente de novo.',
      detail,
      raw,
    );
  }
  // 4xx é pedido malformado, não falha do servidor. Dizer "erro no servidor"
  // aqui empurra a pessoa a repetir uma coisa que nunca vai dar certo.
  if (typeof e?.status === 'number' && e.status >= 400 && e.status < 500) {
    return new AppError('validation', MESSAGES.validation, detail, raw);
  }

  return new AppError('server_error', MESSAGES.server_error, detail || String(raw), raw);
}

/**
 * `navigator.onLine` é DICA, nunca prova (SKILL §10.3).
 * A prova é o request funcionar. Usamos isto só para reduzir a frequência de
 * tentativa, nunca para decidir se tenta.
 */
export function probablyOffline(): boolean {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}
